import api from '@lib/axios';
import type { Conversation, Message, PaginatedResponse } from '@witzy/shared';


export interface ConversationQueryParams {
  page?: number;
  limit?: number;
  status?: 'open' | 'resolved' | 'snoozed' | 'blocked';
  assigned_to?: string;
  search?: string;
  label?: string;
}

export interface ConversationDetail extends Conversation {
  messages_count: number;
  first_message_at: string | null;
  notes: string[];
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  before?: string;
}

export interface SendMessageDto {
  type: 'text' | 'image' | 'document' | 'video' | 'audio' | 'template';
  content: Record<string, unknown>;
  /** wa_message_id of the message being quoted/replied to (Meta `context`). */
  reply_to?: string;
}

export interface SendTemplateDto {
  template_id: string;
  language: string;
  components?: Array<Record<string, unknown>>;
}

export interface AiSuggestion {
  id: string;
  text: string;
  confidence: number;
  model: string;
  free_quota_used: boolean;
}

/** Find or create a conversation for a given contact. */
export async function findOrCreateForContact(
  contactId: string,
): Promise<{ id: string }> {
  const { data } = await api.post<{ data: { id: string } }>(
    `/inbox/from-contact/${contactId}`,
  );
  return data.data;
}

/** Paginated conversation list with optional filters. */
export async function getConversations(
  params?: ConversationQueryParams,
): Promise<PaginatedResponse<Conversation>> {
  const { data } = await api.get<{ data: PaginatedResponse<Conversation> }>('/inbox', { params });
  return data.data;
}

/**
 * Org-wide unread message count. Powers the sidebar Inbox badge.
 * Refetched on every realtime `inbox:new_message` and after the
 * user opens a conversation (the backend zeroes that conversation's
 * count on read).
 */
export async function getInboxUnreadCount(): Promise<{ count: number }> {
  const { data } = await api.get<{ data: { count: number } }>('/inbox/unread-count');
  return data.data;
}

/** Full conversation with message count and notes. */
export async function getConversation(id: string): Promise<ConversationDetail> {
  const { data } = await api.get<{ data: ConversationDetail }>(`/inbox/${id}`);
  return data.data;
}

/** Paginated messages within a conversation. */
export async function getMessages(
  conversationId: string,
  params?: PaginationParams,
): Promise<PaginatedResponse<Message>> {
  const { data } = await api.get<{ data: PaginatedResponse<Message> }>(
    `/inbox/${conversationId}/messages`,
    { params },
  );
  return data.data;
}

/** Send a free-form message (text, media) in a session window. */
export async function sendMessage(conversationId: string, dto: SendMessageDto): Promise<Message> {
  const { data } = await api.post<{ data: Message }>(`/inbox/${conversationId}/messages`, dto);
  return data.data;
}

// NOTE: templates are sent via sendMessage() with type:'template' against
// POST /inbox/:id/messages. There is no POST /inbox/:id/template route, so the
// old sendTemplate() helper (which targeted it) was dead — removed.

/** Mark a specific message as read. */
export async function markAsRead(conversationId: string, messageId: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/messages/${messageId}/read`);
}

/** React to a message with an emoji. Pass an empty string to remove a reaction. */
export async function reactToMessage(
  conversationId: string,
  messageId: string,
  emoji: string,
): Promise<{ message_id: string; emoji: string | null }> {
  const { data } = await api.post<{ data: { message_id: string; emoji: string | null } }>(
    `/inbox/${conversationId}/messages/${messageId}/reaction`,
    { emoji },
  );
  return data.data;
}

/** Assign conversation to a team member. */
export async function assign(conversationId: string, agentId: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/assign`, { agent_id: agentId });
}

/** Mark conversation as resolved. */
export async function resolve(conversationId: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/resolve`);
}

/** Re-open a resolved conversation. */
export async function reopen(conversationId: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/reopen`);
}

/** Block this chat's contact; `deleteChat` also removes the chat. */
export async function blockContact(conversationId: string, deleteChat: boolean): Promise<void> {
  await api.post(`/inbox/${conversationId}/block`, { delete_chat: deleteChat });
}

/** Unblock this chat's contact. */
export async function unblockContact(conversationId: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/unblock`);
}

/** Delete a chat and its messages from Witzy (the contact stays). */
export async function deleteConversation(conversationId: string): Promise<void> {
  await api.delete(`/inbox/${conversationId}`);
}

/** Snooze conversation until a specific time. */
export async function snooze(conversationId: string, until: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/snooze`, { until });
}

/** Add a label to a conversation. */
export async function addLabel(conversationId: string, label: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/labels`, { label });
}

/** Remove a label from a conversation. */
export async function removeLabel(conversationId: string, label: string): Promise<void> {
  await api.delete(`/inbox/${conversationId}/labels/${encodeURIComponent(label)}`);
}

/** Add an internal note (not visible to the contact). */
export async function addNote(conversationId: string, note: string): Promise<void> {
  await api.post(`/inbox/${conversationId}/notes`, { note });
}

/** AI-generated reply suggestions based on the conversation context. */
export async function getAiSuggestions(conversationId: string): Promise<AiSuggestion[]> {
  const { data } = await api.get<{ data: AiSuggestion[] }>(
    `/inbox/${conversationId}/ai-suggestions`,
  );
  return data.data;
}
